import { CustomModal, StatusBanner } from '@components';
import { FC } from 'react';
import { t } from 'i18next';
import { Box, Button, FormControl, Typography } from '@mui/material';
import { Person } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useStatusChangeConfirmationModal } from '@views/pages/Patient/components/DetailsCard/components/StatusChangeConfirmationModal/hooks';

interface StatusChangeConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  handleSaveChanges: () => void;
  name: string;
  date: string;
  isLoading: boolean;
  title?: string;
  warningText?: string;
  warningSubText?: string;
  confirmActionText?: string;
  cancelActionText?: string;
}

export const StatusChangeConfirmationModal: FC<
  StatusChangeConfirmationModalProps
> = ({
  isOpen,
  onClose,
  handleSaveChanges,
  name,
  date,
  isLoading,
  title = t('patient.detailsCard.editPatientInfo.confirmationModal.title'),
  warningSubText = t(
    'patient.detailsCard.editPatientInfo.confirmationModal.warningSubText',
  ),
  warningText = t(
    'patient.detailsCard.editPatientInfo.confirmationModal.warning',
  ),
  confirmActionText = t(
    'patient.detailsCard.editPatientInfo.confirmationModal.deactivate',
  ),
  cancelActionText = t(
    'patient.detailsCard.editPatientInfo.confirmationModal.keepPatient',
  ),
}) => {
  const { dateOfBirth, setDateOfBirth } = useStatusChangeConfirmationModal();

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      disableCloseOnOutsideClick
      title={title}
      isLoading={isLoading}
    >
      <StatusBanner
        text={warningText}
        subText={warningSubText}
        variant='warning'
      />
      <Typography variant='body1' sx={{ mt: 3 }}>
        {t(
          'patient.detailsCard.editPatientInfo.confirmationModal.pleaseVerify',
        )}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Person color='primary' />
        <Typography variant='subtitle1'>{`${name} (${date})`}</Typography>
      </Box>
      <Typography variant='body1' sx={{ mb: 1 }}>
        {t('patient.detailsCard.editPatientInfo.confirmationModal.toConfirm', {
          name,
        })}
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <FormControl fullWidth>
          <DatePicker
            label={t(
              'patient.detailsCard.editPatientInfo.confirmationModal.dateOfBirth',
            )}
            value={dateOfBirth}
            maxDate={dayjs(new Date())}
            onChange={date => {
              setDateOfBirth(dayjs(date));
            }}
          />
        </FormControl>
      </LocalizationProvider>
      <Box
        sx={{
          my: 3,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
        }}
      >
        <Button
          variant='outlined'
          onClick={handleSaveChanges}
          loading={isLoading}
          disabled={date !== dayjs(dateOfBirth).format('MMM DD, YYYY')}
        >
          {confirmActionText}
        </Button>
        <Button
          variant='contained'
          color='secondary'
          sx={{ color: 'white' }}
          loading={isLoading}
          onClick={onClose}
        >
          {cancelActionText}
        </Button>
      </Box>
    </CustomModal>
  );
};
